import { useEffect, useRef, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { MoveRight, ChevronDown } from "lucide-react";
import LiveContextWidget from "@/components/LiveContextWidget";
import { NALJURI_PHOTOS } from "@/lib/photos";

// Cinematic owner-shot footage — muted autoplay, looped (WhatsApp Video 2026-04-03 at 4.34.17 PM)
const HERO_VIDEO = NALJURI_PHOTOS.NATURE_VIDEO;
// Real misty hills view captured FROM the Naljuri Inn resort — poster + fallback
const HERO_POSTER = NALJURI_PHOTOS.HILLS_FROM_RESORT;

// Always-on Bangla affection line for local guests
const BN_TAGLINE = "জাফলং ও সাদাপাথরের তীরে আভিজাত্যের ছোঁয়া।";

export default function Hero() {
  const { t, lang } = useLang();
  const [scrollY, setScrollY] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Safari/iOS sometimes blocks autoplay; nudge once on first user interaction
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => { v.play().catch(() => {}); };
    tryPlay();
    const onInteract = () => { tryPlay(); window.removeEventListener("touchstart", onInteract); window.removeEventListener("click", onInteract); };
    window.addEventListener("touchstart", onInteract, { once: true, passive: true });
    window.addEventListener("click", onInteract, { once: true });
    return () => {
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("click", onInteract);
    };
  }, []);

  // Depth parallax — two layers moving at different speeds for subtle 3D
  const farY  = scrollY * 0.10;
  const nearY = scrollY * 0.28;

  return (
    <section className="relative min-h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Far layer — poster photo (fallback + shows under the video on ultra-slow networks) */}
      <div className="absolute inset-0 will-change-transform"
           style={{ transform: `translate3d(0, ${farY}px, 0) scale(1.08)` }}>
        <img src={HERO_POSTER}
             alt="Misty Meghalaya hills viewed from Naljuri Inn — luxury resort in Jaflong, Sylhet"
             className="w-full h-[120%] object-cover" />
      </div>

      {/* Near layer — cinematic video (muted, looped, autoplay), parallaxes slightly faster */}
      <div className="absolute inset-0 will-change-transform"
           style={{ transform: `translate3d(0, ${nearY}px, 0) scale(1.06)` }}>
        <video ref={videoRef}
               data-testid="hero-bg-video"
               autoPlay muted loop playsInline preload="auto"
               poster={HERO_POSTER}
               onCanPlay={() => setVideoReady(true)}
               className={`w-full h-[120%] object-cover transition-opacity duration-700 ${videoReady ? 'opacity-100' : 'opacity-0'}`}>
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
      </div>

      {/* Editorial dark overlays for cinematic contrast + white-text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-forest-900/70 via-forest-900/55 to-forest-900/95 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest-900/65 via-transparent to-forest-900/30 pointer-events-none" />
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      {/* Live Context Widget — top-right corner (mobile + desktop) */}
      <LiveContextWidget />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-48 md:pt-52 pb-32 min-h-screen flex flex-col">
        <div className="max-w-4xl">
          <p className="eyebrow text-gold-400 fade-up flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="inline-block w-7 h-px bg-gold-400/80" />
            {t.hero.eyebrow}
          </p>
          <h1 className={`mt-5 fade-up delay-1 ${lang==='bn' ? 'font-bn' : 'font-display'}`}
              data-testid="hero-headline"
              style={{ fontSize: 'clamp(2.1rem, 6.2vw, 5.4rem)', lineHeight: 1.05, letterSpacing: '-0.015em' }}>
            <span className="block text-cream-100">{t.hero.title_1}</span>
            <span className="block italic font-light shimmer-text mt-1">{t.hero.title_2}</span>
          </h1>

          {/* Bangla affection line — always visible (local touch) */}
          <p className="mt-4 font-bn text-base md:text-lg text-sage-100/95 fade-up delay-1"
             data-testid="hero-bn-tagline">
            {BN_TAGLINE}
          </p>

          <p className="mt-6 text-sm md:text-lg text-cream-100/90 max-w-2xl leading-relaxed fade-up delay-2"
             data-testid="hero-sub">
            {t.hero.sub}
          </p>

          <div className="mt-8 md:mt-10 flex flex-wrap gap-3 md:gap-4 fade-up delay-3">
            <a href="#booking"
               data-testid="hero-book-cta"
               className="group inline-flex items-center gap-2 bg-gold-600 text-cream-100 px-7 md:px-8 py-3.5 md:py-4 rounded-full text-sm tracking-wide hover:bg-gold-700 transition-all hover:gap-4 border border-gold-500/40 shadow-[0_24px_50px_-20px_rgba(184,134,11,0.6)]">
              {t.hero.cta_book} <MoveRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#stay"
               data-testid="hero-explore-cta"
               className="inline-flex items-center gap-2 border border-sage-300/50 text-cream-100 px-7 md:px-8 py-3.5 md:py-4 rounded-full text-sm tracking-wide hover:bg-sage-100/10 hover:border-sage-200 transition-colors">
              {t.hero.cta_explore}
            </a>
          </div>
        </div>

        <div className="mt-auto hidden md:flex items-center justify-between text-cream-100/70 fade-up delay-4">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest2">
            <ChevronDown className="w-4 h-4 animate-bounce text-sage-300" />
            {t.hero.scroll}
          </div>
          <div className="flex gap-12 text-right">
            <Stat n="15+" l="Premium suites" />
            <Stat n="3km" l="To Jaflong" />
            <Stat n="24/7" l="Concierge" />
          </div>
        </div>
      </div>
    </section>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="text-3xl md:text-4xl font-display text-cream-100 leading-none">{n}</div>
    <div className="text-[10px] uppercase tracking-widest2 mt-2 text-sage-200/90">{l}</div>
  </div>
);
